import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from './AuthContext';
import { monitoringWebSocket } from '../services/websocketService';
import {
  OPERATOR_MESSAGE_CATEGORIES,
  OperatorMessage,
  OperatorMessageCategory,
  operatorMessageService,
} from '../services/operatorMessageService';
import { formatDateTimeKst } from '../utils/dateUtils';
import OperatorMessageComposeTime from '../components/monitoring/OperatorMessageComposeTime';

interface OperatorMessageContextValue {
  unreadCount: number;
  refreshUnread: () => Promise<void>;
  openCompose: () => void;
}

const OperatorMessageContext = createContext<OperatorMessageContextValue>({
  unreadCount: 0,
  refreshUnread: async () => {},
  openCompose: () => {},
});

export const useOperatorMessages = () => useContext(OperatorMessageContext);

const emptyForm = {
  category: 'notice' as OperatorMessageCategory,
  title: '',
  body: '',
};

export const OperatorMessageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authState } = useAuth();
  const authenticated = Boolean(authState.isAuthenticated);
  const [queue, setQueue] = useState<OperatorMessage[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const current = queue[0] || null;

  const refreshUnread = useCallback(async () => {
    if (!authenticated) {
      setQueue([]);
      return;
    }
    try {
      const list = await operatorMessageService.listUnread();
      setQueue(list);
    } catch {
      /* ignore transient */
    }
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) {
      setQueue([]);
      return;
    }
    refreshUnread();
    const t = setInterval(refreshUnread, 20000);
    return () => clearInterval(t);
  }, [authenticated, refreshUnread]);

  useEffect(() => {
    if (!authenticated) return;

    const onMsg = (payload: any) => {
      const data = payload?.data ?? payload;
      if (data?.deleted || data?.deleted_all) {
        refreshUnread();
        return;
      }
      if (data?.id && data?.title) {
        setQueue((prev) => {
          if (prev.some((p) => p.id === data.id)) return prev;
          if (data.is_read) return prev;
          return [...prev, data as OperatorMessage];
        });
      } else {
        refreshUnread();
      }
    };

    monitoringWebSocket.subscribe('operator_message', onMsg);
    return () => monitoringWebSocket.unsubscribe('operator_message', onMsg);
  }, [authenticated, refreshUnread]);

  const ackCurrent = async () => {
    if (!current) return;
    try {
      await operatorMessageService.markRead(current.id);
    } catch {
      /* still remove locally to avoid stuck */
    }
    setQueue((prev) => prev.filter((m) => m.id !== current.id));
  };

  const openCompose = () => {
    setForm(emptyForm);
    setComposeOpen(true);
  };

  const submitCompose = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      await operatorMessageService.create(form);
      setComposeOpen(false);
      await refreshUnread();
    } finally {
      setSaving(false);
    }
  };

  const value = useMemo(
    () => ({
      unreadCount: queue.length,
      refreshUnread,
      openCompose,
    }),
    [queue.length, refreshUnread]
  );

  return (
    <OperatorMessageContext.Provider value={value}>
      {children}

      <Dialog
        open={Boolean(current)}
        disableEscapeKeyDown
        onClose={() => {
          /* must ack */
        }}
        fullWidth
        maxWidth="sm"
      >
        {current && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="primary" label={current.category_label || current.category} />
                <Typography variant="h6" component="span">
                  {current.title}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {(current.author_full_name || current.author_username || '관제원') +
                  ' · ' +
                  (current.created_at ? formatDateTimeKst(current.created_at) : '')}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{current.body}</Typography>
              {queue.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  미확인 {queue.length}건 중 1번째
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={ackCurrent} autoFocus>
                확인 (읽음)
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>관제 메시지 작성</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <OperatorMessageComposeTime />
            <FormControl fullWidth>
              <InputLabel id="om-compose-cat">카테고리</InputLabel>
              <Select
                labelId="om-compose-cat"
                label="카테고리"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as OperatorMessageCategory,
                  }))
                }
              >
                {OPERATOR_MESSAGE_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="제목"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              minRows={4}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>취소</Button>
          <Button variant="contained" onClick={submitCompose} disabled={saving}>
            발송
          </Button>
        </DialogActions>
      </Dialog>
    </OperatorMessageContext.Provider>
  );
};
